import { Container, Sprite, Texture } from "pixi.js"
import { TileSize } from "game-common/models"
import { roughToFineCoordinates } from "game-common/util"
import { SpritePool } from "./sprite_pool"

export interface SpriteManagementServiceConfig {
    spritePoolSize: number
    defaultSurface: Container
}

export class SpriteManagementService {
    private spritePool: SpritePool
    private defaultSurface: Container

    constructor(config: SpriteManagementServiceConfig) {
        this.spritePool = new SpritePool(config.spritePoolSize)
        this.defaultSurface = config.defaultSurface
    }

    /**
     * Attach a texture to a sprite and add it to the specified surface
     */
    attachTexture = (texture: Texture, surface?: Container): Sprite => {
        const spritePoolItem = this.spritePool.checkout()
        const sprite = spritePoolItem.data
        sprite.texture = texture
        // Reset the occluder-fade tree tag on every checkout so a recycled sprite can't keep a stale
        // "is a tree" flag from a prior use; CompositeTileRenderer flips it back on for tree tiles.
        ;(sprite as any).occluderTreeTile = false
        const surfaceToUse = surface || this.defaultSurface
        surfaceToUse.addChild(sprite)
        spritePoolItem.data = sprite

        return sprite
    }

    /**
     * Creates a sprite at the specified coordinates with consistent positioning and z-index calculation
     */
    createSpriteAt = (
        tilesetTileId: string,
        x: number,
        y: number,
        zIndexOffset: number = 0,
        surface?: Container,
    ): Sprite => {
        const texture = Texture.from(`${tilesetTileId}`)
        const sprite = this.attachTexture(texture, surface)
        const fine = roughToFineCoordinates({ x, y })
        sprite.x = fine.x
        sprite.y = fine.y
        sprite.zIndex = 100 + zIndexOffset + fine.y + TileSize / 3
        return sprite
    }

    /**
     * Release all sprites back to the pool
     */
    releaseAllSprites = (onRelease?: (spriteItemObject: { data: Sprite }) => void): number => {
        return this.spritePool.releaseAll(spriteItemObject => {
            const sprite = spriteItemObject.data
            if (!sprite.parent) {
                spriteItemObject.data = new Sprite()
                return true // Indicates error
            } else {
                sprite.parent?.removeChild(sprite)
                return false // No error
            }
        })
    }

    /**
     * Get the current sprite pool for advanced operations
     */
    getSpritePool = (): SpritePool => {
        return this.spritePool
    }

    /**
     * Clean up resources
     */
    destroy = (): void => {
        this.spritePool.releaseAll()
    }
}
